<script setup lang="ts">
import { refDebounced } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { useTicketSettingsStore } from '../../stores/ticketSettingsStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import type {
  ITicketWorkGroupDetail,
  ITicketWorkGroupPerformer,
} from '../../types/settings/ITicketCategorySettings'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { parseFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'

const props = defineProps<{
  modelValue: boolean
  groupId: number | string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'saved': [group: ITicketWorkGroupDetail]
}>()

const ticketSettingsStore = useTicketSettingsStore()
const feedbackStore = useFeedbackStore()
const apiStore = useApiStore()

const employeeModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

const formData = ref<{
  name: string
  description: string
  performers: ITicketWorkGroupPerformer[]
}>({
  name: '',
  description: '',
  performers: [],
})

const lastSavedName = ref('')
const lastSavedDescription = ref('')

const searchQuery = ref('')
const searchDebounced = refDebounced(searchQuery, 300)
const selectedPerformerIds = ref<string[]>([])

const errors = ref<{
  name?: string
}>({})

const isEditMode = computed(() => !!props.groupId)
const title = computed(() => isEditMode.value ? 'Редактирование группы исполнителей' : 'Создание группы исполнителей')

const fallbackAvatars = new Map<string, string>()

function getAvatarUrl(p: { id?: string; avatar?: string | null }): string {
  if (p.avatar) return p.avatar
  const id = String(p.id || 'default')
  if (!fallbackAvatars.has(id))
    fallbackAvatars.set(id, getRandomAvatar())
  return fallbackAvatars.get(id)!
}

// Поиск по списку добавленных участников
const filteredPerformers = computed(() => {
  const query = searchDebounced.value.trim().toLowerCase()
  if (!query) return formData.value.performers

  return formData.value.performers.filter(p => {
    const fullName = [p.last_name, p.first_name, p.middle_name].filter(Boolean).join(' ').toLowerCase()
    const pos = (p.position_name || '').toLowerCase()
    const dept = (p.department_name || '').toLowerCase()
    return fullName.includes(query) || pos.includes(query) || dept.includes(query)
  })
})

// Логика чекбоксов "Выбрать всех"
const isAllSelected = computed(() => {
  if (!formData.value.performers.length) return false
  return formData.value.performers.every(p => selectedPerformerIds.value.includes(String(p.id)))
})

const isSomeSelected = computed(() => {
  return selectedPerformerIds.value.length > 0 && !isAllSelected.value
})

function toggleSelectAll() {
  if (isAllSelected.value) {
    selectedPerformerIds.value = []
  } else {
    selectedPerformerIds.value = formData.value.performers.map(p => String(p.id))
  }
}

function togglePerformerSelection(id: string) {
  const strId = String(id)
  const idx = selectedPerformerIds.value.indexOf(strId)
  if (idx >= 0) {
    selectedPerformerIds.value.splice(idx, 1)
  } else {
    selectedPerformerIds.value.push(strId)
  }
}

async function handleRemovePerformer(id: string) {
  const strId = String(id)
  const nextPerformers = formData.value.performers.filter(p => String(p.id) !== strId)
  formData.value.performers = nextPerformers
  selectedPerformerIds.value = selectedPerformerIds.value.filter(item => item !== strId)

  if (isEditMode.value && props.groupId) {
    try {
      await ticketSettingsStore.updateWorkGroup(props.groupId, {
        performers: nextPerformers.map(p => ({ id: String(p.id) })),
      })
    } catch {
      // Ошибка отобразится тостом в store
    }
  }
}

async function removeSelectedPerformers() {
  const toRemove = new Set(selectedPerformerIds.value)
  const nextPerformers = formData.value.performers.filter(p => !toRemove.has(String(p.id)))
  formData.value.performers = nextPerformers
  selectedPerformerIds.value = []

  if (isEditMode.value && props.groupId) {
    try {
      await ticketSettingsStore.updateWorkGroup(props.groupId, {
        performers: nextPerformers.map(p => ({ id: String(p.id) })),
      })
    } catch {
      // Ошибка отобразится тостом в store
    }
  }
}

async function openEmployeeSelectModal() {
  if (!apiStore.employees?.length) {
    await apiStore.fetchAllEmployees()
  }
  employeeModalRef.value?.open()
}

async function onEmployeesSelected(selected: IEmployeeTreeItem[]) {
  const currentIds = new Set(formData.value.performers.map(p => String(p.id)))
  const newPerformers: ITicketWorkGroupPerformer[] = []

  for (const emp of selected) {
    const strId = String(emp.id)
    if (!currentIds.has(strId)) {
      currentIds.add(strId)
      const { first_name, last_name, middle_name } = parseFullName(emp.full_name || '')
      newPerformers.push({
        id: strId,
        first_name: first_name || '',
        last_name: last_name || '',
        middle_name: middle_name || '',
        avatar: (emp as any).avatar || null,
        position_name: emp.position_name || null,
        department_name: (emp as any).department_name || null,
      })
    }
  }

  const updatedList = [...formData.value.performers, ...newPerformers]
  formData.value.performers = updatedList

  if (isEditMode.value && props.groupId && newPerformers.length > 0) {
    try {
      await ticketSettingsStore.updateWorkGroup(props.groupId, {
        performers: updatedList.map(p => ({ id: String(p.id) })),
      })
    } catch {
      // Ошибка отобразится тостом в store
    }
  }
}

async function onFieldBlur(field: 'name' | 'description') {
  if (!isEditMode.value || !props.groupId) return

  if (field === 'name') {
    const val = formData.value.name.trim()
    if (!val) {
      errors.value.name = 'Наименование группы обязательно для заполнения'
      return
    }
    if (val.length > 128) {
      errors.value.name = 'Наименование группы не должно превышать 128 символов'
      return
    }
    errors.value.name = undefined

    if (val !== lastSavedName.value) {
      try {
        await ticketSettingsStore.updateWorkGroup(props.groupId, { name: val })
        lastSavedName.value = val
      } catch {
        // Ошибка в store
      }
    }
  } else if (field === 'description') {
    const val = formData.value.description.trim()
    if (val !== lastSavedDescription.value) {
      try {
        await ticketSettingsStore.updateWorkGroup(props.groupId, { description: val })
        lastSavedDescription.value = val
      } catch {
        // Ошибка в store
      }
    }
  }
}

// Загрузка деталей при открытии диалога
watch(
  () => [props.modelValue, props.groupId] as const,
  async ([isOpen, gId]) => {
    errors.value = {}
    searchQuery.value = ''
    selectedPerformerIds.value = []
    if (isOpen) {
      if (gId) {
        const detail = await ticketSettingsStore.fetchWorkGroupDetail(gId)
        if (detail) {
          formData.value = {
            name: detail.name || '',
            description: detail.description || '',
            performers: detail.performers ? [...detail.performers] : [],
          }
          lastSavedName.value = detail.name || ''
          lastSavedDescription.value = detail.description || ''
        }
      } else {
        formData.value = {
          name: '',
          description: '',
          performers: [],
        }
        lastSavedName.value = ''
        lastSavedDescription.value = ''
      }
    }
  },
  { immediate: true },
)

function handleClose() {
  emit('update:modelValue', false)
  errors.value = {}
  searchQuery.value = ''
  selectedPerformerIds.value = []
}

function validate(): boolean {
  errors.value = {}
  if (!formData.value.name.trim()) {
    errors.value.name = 'Наименование группы обязательно для заполнения'
    return false
  }
  if (formData.value.name.trim().length > 128) {
    errors.value.name = 'Наименование группы не должно превышать 128 символов'
    return false
  }
  return true
}

async function handleCreateGroup() {
  if (!validate()) return

  const payload = {
    name: formData.value.name.trim(),
    description: formData.value.description.trim(),
    performers: formData.value.performers.map(p => ({ id: String(p.id) })),
  }

  try {
    const saved = await ticketSettingsStore.createWorkGroup(payload)
    if (saved) {
      emit('saved', saved)
      handleClose()
    }
  } catch {
    // Ошибка отобразится тостом в store
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="600"
    max-height="90vh"
    scrollable
    @click:outside="handleClose"
    @keydown.esc.stop="handleClose"
  >
    <VCard class="pa-6 d-flex flex-column ga-4 bg-surface rounded-lg" style="max-height: 85vh;">
      <!-- Шапка диалога -->
      <div class="d-flex align-start justify-space-between">
        <div class="d-flex flex-column">
          <span class="text-h4 font-weight-bold">
            {{ title }}
          </span>
        </div>
        <CustomCloseButton :close-action="handleClose" />
      </div>

      <!-- Поля формы -->
      <div class="d-flex flex-column ga-4">
        <!-- Наименование группы -->
        <VTextField
          v-model="formData.name"
          placeholder="Введите наименование группы"
          variant="outlined"
          clearable
          persistent-placeholder
          :error="!!errors.name"
          :error-messages="errors.name"
          hide-details="auto"
          @click:clear="formData.name = ''"
          @blur="onFieldBlur('name')"
        >
          <template #label>
            <span>Наименование группы<sup class="required-label">*</sup></span>
          </template>
        </VTextField>

        <!-- Описание -->
        <VTextarea
          v-model="formData.description"
          placeholder="Краткое описание назначения группы"
          variant="outlined"
          density="comfortable"
          persistent-placeholder
          rows="3"
          hide-details
          @blur="onFieldBlur('description')"
        >
          <template #label>
            <span>Описание группы</span>
          </template>
        </VTextarea>
      </div>

      <!-- Панель массовых действий при выделении сотрудников -->
      <div
        v-if="selectedPerformerIds.length > 0"
        class="d-flex align-center justify-space-between px-3 py-2 rounded-lg selected-action-bar ga-2"
      >
        <div class="d-flex align-center ga-2">
          <VCheckbox
            :model-value="isAllSelected"
            :indeterminate="isSomeSelected"
            density="compact"
            hide-details
            @click.stop="toggleSelectAll"
          />
          <span class="text-body-2 font-weight-semibold text-primary">
            Выбрано {{ selectedPerformerIds.length }} чел.
          </span>
        </div>
        <div class="d-flex align-center ga-2">
          <VBtn
            size="small"
            color="error"
            variant="tonal"
            prepend-icon="ri-delete-bin-line"
            @click="removeSelectedPerformers"
          >
            Удалить
          </VBtn>
        </div>
      </div>

      <!-- Поиск по добавленным участникам -->
      <div v-if="formData.performers.length > 0">
        <VTextField
          v-model="searchQuery"
          variant="outlined"
          density="compact"
          clearable
          :append-inner-icon="searchQuery ? undefined : 'ri-search-line'"
          placeholder="Поиск по ФИО, телефону или должности"
          rounded="pill"
          class="search-field"
          hide-details
        />
      </div>

      <!-- Заголовок списка с чекбоксом Выбрать всех -->
      <div
        v-if="formData.performers.length > 0 && selectedPerformerIds.length === 0"
        class="d-flex align-center justify-space-between px-2 pt-1 pb-0"
      >
        <div class="d-flex align-center ga-2 cursor-pointer" @click="toggleSelectAll">
          <VCheckbox
            :model-value="isAllSelected"
            :indeterminate="isSomeSelected"
            density="compact"
            hide-details
            @click.stop="toggleSelectAll"
          />
          <span class="text-caption text-medium-emphasis select-none">
            Выбрать всех
          </span>
        </div>
      </div>

      <!-- Список участников -->
      <div class="overflow-y-auto flex-grow-1 custom-employee-list">
        <div
          v-if="formData.performers.length === 0"
          class="d-flex flex-column align-center justify-center py-6 text-medium-emphasis ga-2"
        >
          <VIcon icon="ri-user-search-line" size="40" class="opacity-50" />
          <div class="text-body-1 font-weight-medium">
            Сотрудники не выбраны
          </div>
        </div>

        <VList v-else class="pa-0 bg-transparent">
          <VListItem
            v-for="emp in filteredPerformers"
            :key="emp.id"
            class="px-2 py-2 rounded-lg mb-1 transition-colors employee-row cursor-pointer"
            :class="{ 'employee-row--selected': selectedPerformerIds.includes(String(emp.id)) }"
            @click="togglePerformerSelection(String(emp.id))"
          >
            <template #prepend>
              <div class="d-flex align-center me-2" @click.stop>
                <VCheckbox
                  :model-value="selectedPerformerIds.includes(String(emp.id))"
                  density="compact"
                  hide-details
                  class="me-1"
                  @click.stop="togglePerformerSelection(String(emp.id))"
                />
                <VAvatar size="40" class="border" :image="getAvatarUrl(emp)" />
              </div>
            </template>

            <VListItemTitle class="font-weight-medium text-body-1 text-high-emphasis">
              {{ [emp.last_name, emp.first_name, emp.middle_name].filter(Boolean).join(' ') || 'Сотрудник' }}
            </VListItemTitle>

            <VListItemSubtitle class="text-caption text-medium-emphasis mt-0.5">
              <span v-if="emp.position_name" class="font-weight-medium text-medium-emphasis">
                {{ emp.position_name }}
              </span>
              <span v-if="emp.department_name">
                <span v-if="emp.position_name"> • </span>{{ emp.department_name }}
              </span>
            </VListItemSubtitle>

            <template #append>
              <VTooltip location="top" text="Удалить из группы">
                <template #activator="{ props: tooltipProps }">
                  <VIconBtn
                    v-bind="tooltipProps"
                    :rounded="false"
                    :size="32"
                    icon="ri-delete-bin-3-line"
                    variant="text"
                    color="error"
                    @click.stop="handleRemovePerformer(emp.id)"
                  />
                </template>
              </VTooltip>
            </template>
          </VListItem>
        </VList>
      </div>

      <!-- Кнопка добавления сотрудников / создания группы -->
      <div class="pt-2">
        <VBtn
          v-if="isEditMode"
          color="primary"
          prepend-icon="ri-user-add-line"
          block
          :loading="feedbackStore.isGlobalLoading"
          @click="openEmployeeSelectModal"
        >
          Добавить сотрудников
        </VBtn>

        <div v-else class="d-flex flex-column ga-2">
          <VBtn
            variant="outlined"
            color="primary"
            prepend-icon="ri-user-add-line"
            block
            @click="openEmployeeSelectModal"
          >
            Добавить сотрудников
          </VBtn>
          <VBtn
            color="primary"
            block
            :loading="feedbackStore.isGlobalLoading"
            @click="handleCreateGroup"
          >
            Создать группу
          </VBtn>
        </div>
      </div>
    </VCard>

    <!-- Модалка выбора сотрудников -->
    <EmployeeSelectionModal
      ref="employeeModalRef"
      :employees="apiStore.employees"
      multiple
      title="Добавить участников в группу"
      apply-label="Добавить"
      @select-multiple="onEmployeesSelected"
    />
  </VDialog>
</template>

<style scoped lang="scss">
:deep(.search-field.v-text-field .v-field) {
  border-radius: 500px !important;
}

.selected-action-bar {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.employee-row {
  transition: background-color 0.15s ease-in-out;
  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.04);
  }

  &--selected {
    background-color: rgba(var(--v-theme-primary), 0.08) !important;
  }
}

.custom-employee-list {
  padding-inline-end: 4px;
}
</style>
